import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { distinctUntilChanged, map } from 'rxjs';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { ApiResponse, Metadata } from '../../../../api/api-types/api-response.api';
import {
	AssignmentEndOfCare,
	AssignmentWithRelations,
	UpdateAssignmentBody,
} from '../../../../api/app-types/assignment';
import { Client } from '../../../../api/app-types/client.app';
import { DisabilityKind } from '../../../../api/app-types/disability-type.app';
import { DocumentFile, DocumentFileSubjectType } from '../../../../api/app-types/document.app';
import { LegalFoundation } from '../../../../api/app-types/legal-foundation.app';
import { DB_PHASES } from '../../../../api/app-types/phase.app';
import { ClarificationValue } from '../../../../api/app-types/relative.app';
import { Sponsor } from '../../../../api/app-types/sponsor.app';
import { TypeOfCare } from '../../../../api/app-types/type-of-care';
import { DateRange } from '../../../../api/app-types/validation.app';
import { CATEGORIES_API } from '../../../../api/injection-tokens/services.token';
import { ASSIGNMENT_API } from '../../../../api/services/assignment.service';
import { LEGAL_FOUNDATION_SERVICE } from '../../../../api/services/legal-foundation.service';
import { SPONSOR_SERVICE } from '../../../../api/services/sponsor.service';
import { provideUploadApi } from '../../../../api/services/upload.api';
import { VALIDATION_SERVICE } from '../../../../api/services/validation.service';
import { mapEditFormFieldNamesToApiAssignmentFieldNames } from '../../../../api/transformers/assignment.transform';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { LvrRedirectNav } from '../../../../core/classes/redirect-nav.class';
import { LvrDataProtection } from '../../../../core/components/data-protection/data-protection';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrSection } from '../../../../core/components/section/section';
import { LvrSectionForm } from '../../../../core/components/section/section-form';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { STATE } from '../../../../core/services/state.service';
import { DocumentUploadControl } from '../../../../shared/document-upload/document-upload';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../../shared/document-upload/document-upload-validators';
import { matDateProviders } from '../../../../shared/helpers/date.func';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { ClientResolved } from '../../../../shared/routing/resolvers/client.resolver';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { maxDateValidator, minDateValidator } from '../../../../shared/validators/max-date-validator';

@Component({
	selector: 'lvr-fds-assignment-edit',
	templateUrl: './fds-assignment-edit.html',
	styleUrl: './fds-assignment-edit.scss',
	imports: [
		DatePipe,
		ReactiveFormsModule,
		LvrPaddingDirective,
		LvrField,
		MatFormFieldModule,
		MatInput,
		MatSelectModule,
		LvrSection,
		LvrSectionForm,
		LvrInputDirective,
		DocumentUploadControl,
		LvrDefaultActions,
		LvrRestoreActions,
		LvrAutoSaveDirective,
		TranslocoPipe,
		RouterLink,
		LvrDataProtection,
		MatDatepickerModule,
		LvrAppendAsteriskPipe,
	],
	providers: [provideUploadApi, matDateProviders],
})
export class FdsAssignmentEdit extends LvrRedirectNav {
	private readonly assignmentApi = inject(ASSIGNMENT_API);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly validationApi = inject(VALIDATION_SERVICE);
	private readonly categoriesApi = inject(CATEGORIES_API);
	private readonly sponsorService = inject(SPONSOR_SERVICE);
	private readonly legalFoundationService = inject(LEGAL_FOUNDATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly draftService = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	private readonly state = inject(STATE);
	private readonly environment = inject(ENVIRONMENT);
	private readonly clientResolved = this.resolveDataAsSignal<ClientResolved | undefined>('client');
	public readonly client = computed<Client | undefined>(() => this.clientResolved()?.value as Client | undefined);
	public readonly clientId = this.resolveDataAsSignal<number | string>('clientId');
	public readonly assignmentWithMetadata =
		this.resolveDataAsSignal<ApiResponse<AssignmentWithRelations | undefined, Metadata>>('assignmentWithMetadata');
	public readonly assignment = computed<AssignmentWithRelations | undefined>(() => this.assignmentWithMetadata()?.data);
	public readonly assignmentMetadata = computed<Metadata | undefined>(() => this.assignmentWithMetadata()?.metadata);
	public readonly assignmentId = computed(() => this.assignment()?.id ?? -1);
	public readonly isEditable = computed(() => true);
	public readonly documentsRequired = signal(false);
	public readonly editableCtrls = computed(() => this.assignmentMetadata()?.editOnlyFields ?? []);
	public readonly typeOfCares = signal<TypeOfCare[]>([]);
	public readonly sponsors = signal<Sponsor[]>([]);
	public readonly availableLegalFoundation = signal<LegalFoundation[]>([]);
	private readonly typeOfCareId = signal<number | undefined>(undefined);
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly dateFormatHint = this.environment.visualDateFormat;
	private readonly fb = inject(FormBuilder);
	public readonly showInitialStatementSelect = signal(false);
	public readonly isLoading = signal(false);

	public hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);

	public readonly minDateOfCommission = () => {
		const offset = this.environment.formConfiguration.assignment.minDateOfCommissionOffsetDays;
		const threshold = new Date();
		threshold.setDate(threshold.getDate() - offset);
		threshold.setHours(0, 0, 0, 0);
		return threshold;
	};
	public readonly maxDateOfCommission = () => {
		const date = new Date();
		return date;
	};

	public readonly isSubmitView = computed(() => {
		const redirectUrl = this.route?.snapshot?.queryParamMap?.get('redirectUrl') ?? '';
		return redirectUrl.includes('/fds-move');
	});
	public readonly isReturn = computed(() => this.isSubmitView() && this.assignment()?.phaseChangeId === DB_PHASES.FP5);
	public readonly isSubmit = computed(() => this.isSubmitView() && this.assignment()?.phaseChangeId === DB_PHASES.FP4);
	public readonly endOfCareLabelKey = computed(() =>
		this.isReturn() ? 'assignment.fds.return_reason_label' : 'assignment.edit.type_of_statement',
	);

	private readonly disabilityKindsPage =
		this.resolveDataAsSignal<PaginatedResponse<DisabilityKind>>('disabilityKindsPage');
	public readonly disabilityKinds = computed(() => (this.disabilityKindsPage()?.entities ?? []) as DisabilityKind[]);
	private readonly endOfCarePage = this.resolveDataAsSignal<PaginatedResponse<AssignmentEndOfCare>>('endOfCares');
	public readonly endOfCares = computed(() => (this.endOfCarePage()?.entities ?? []) as AssignmentEndOfCare[]);

	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly subjectType = DocumentFileSubjectType.ASSIGNMENT;
	public readonly maxFileCount = this.environment.contactSettings.maxFiles;
	public readonly maxFileSize = this.environment.fileSettings.maxFileSize;
	public readonly canEditAssignmentsPermission = hasPermission(this.session.permissions, 'Assignments.Edit');
	public readonly canManageAssignmentDocuments = hasPermission(this.session.permissions, 'Documents.Assignment.Edit');

	public readonly draftId = computed(() => `${FORM_KEYS.EDIT_FDS}.${this.assignmentId()}`);

	public readonly form = inject(FormBuilder).group({
		dateOfCommission: [
			null as Date | null,
			[Validators.required, minDateValidator(this.minDateOfCommission()), maxDateValidator(this.maxDateOfCommission())],
		],
		sponsorId: [null as number | null, [Validators.required]],
		legalFoundationId: [null as number | null, [Validators.required]],
		initialStatement: this.fb.control<boolean | null>(null),
		careRelatedHandicapId: [null as number | null],
		endOfCareId: [null as number | null],
		documents: inject(FormBuilder).nonNullable.control<DocumentFile[]>(
			[],
			[
				maxFiles(this.maxFileCount),
				maxFileSize(this.maxFileSize),
				allowedFileTypes(this.environment.fileSettings.allowedMimeTypes),
			],
		),
		socialDataProtectionInfo: [false],
		clarification: [this.getEffectiveClarification(undefined) as ClarificationValue],
	});

	public readonly clarificationDateRange = signal<DateRange | undefined>(undefined);
	public readonly clarificationMinDate = computed(() => {
		return this.clarificationDateRange()?.min;
	});
	public readonly clarificationMaxDate = computed(() => {
		return this.clarificationDateRange()?.max;
	});

	private readonly sponsorId = toSignal(
		this.form.get('sponsorId')!.valueChanges.pipe(
			distinctUntilChanged(),
			map(() => this.form.get('sponsorId')!.value as number | undefined),
		),
		{
			initialValue: undefined,
		},
	);

	private readonly legalFoundationId = toSignal(
		this.form.get('legalFoundationId')!.valueChanges.pipe(
			distinctUntilChanged(),
			map(() => this.form.get('legalFoundationId')!.value as number | undefined),
		),
		{
			initialValue: undefined,
		},
	);

	public constructor() {
		super();
		void this.loadTypeOfCares();

		// Patch assignment values
		effect(() => {
			const assignment = this.assignment() as AssignmentWithRelations & { documents?: DocumentFile[] };
			if (!assignment) return;

			const docs = assignment.documents ?? [];

			this.form.patchValue(
				{
					dateOfCommission: assignment.careStartDate ? new Date(assignment.careStartDate) : null,
					sponsorId: assignment.sponsorId ?? null,
					legalFoundationId: assignment.legalFoundationId ?? null,
					careRelatedHandicapId: assignment.careRelatedHandicapId ?? null,
					socialDataProtectionInfo: assignment.socialDataProtectionInfo ?? false,
					clarification: this.getEffectiveClarification(assignment),
					endOfCareId: assignment.endOfCare?.id ?? null,
				},
				{ emitEvent: false },
			);

			this.form.get('documents')?.setValue(docs, { emitEvent: false });

			// Trigger signals based on valueChanges
			this.form.get('sponsorId')?.setValue(assignment.sponsorId ?? null, { emitEvent: true });
			this.form.get('legalFoundationId')?.setValue(assignment.legalFoundationId ?? null, { emitEvent: true });

			this.form.markAsPristine();
		});

		// Load sponsors for FDS type of care
		effect(() => {
			const typeOfCareId = this.typeOfCareId();

			if (!typeOfCareId) {
				this.sponsors.set([]);
				return;
			}

			void this.loadSponsors(typeOfCareId);
		});

		// Load legal foundations for selected sponsor
		effect(() => {
			const typeOfCareId = this.typeOfCareId();
			const sponsorId = this.sponsorId();
			const legalFoundationControl = this.form.get('legalFoundationId');

			if (!typeOfCareId || !sponsorId) {
				this.availableLegalFoundation.set([]);
				legalFoundationControl?.disable({ emitEvent: false });
				return;
			}

			void this.getFilteredLegalFoundations(typeOfCareId, sponsorId);

			if (this.canEditControl('legalFoundationId')) {
				legalFoundationControl?.enable({ emitEvent: false });
			} else {
				legalFoundationControl?.disable({ emitEvent: false });
			}
		});

		// Apply metadata and special editability rules
		effect(() => {
			const meta = this.editableCtrls();

			if (meta.includes('*')) {
				Object.keys(this.form.controls).forEach((key) => {
					this.form.get(key)?.enable({ emitEvent: false });
				});
				return;
			}

			Object.keys(this.form.controls).forEach((key) => {
				const control = this.form.get(key);
				if (!control) return;

				if (this.canEditControl(key)) {
					control.enable({ emitEvent: false });
				} else {
					control.disable({ emitEvent: false });
				}
			});

			const documentsControl = this.form.get('documents');
			if (this.isEditable() && this.canManageAssignmentDocuments()) {
				documentsControl?.enable({ emitEvent: false });
			} else {
				documentsControl?.disable({ emitEvent: false });
			}

			const endOfCareControl = this.form.get('endOfCareId');
			if (this.isSubmitView()) {
				endOfCareControl?.clearValidators();
				endOfCareControl?.enable({ emitEvent: false });
			} else {
				endOfCareControl?.clearValidators();
				endOfCareControl?.disable({ emitEvent: false });
			}
			endOfCareControl?.updateValueAndValidity({ emitEvent: false });

			const legalFoundationControl = this.form.get('legalFoundationId');
			if (!this.sponsorId() || !this.canEditControl('legalFoundationId')) {
				legalFoundationControl?.disable({ emitEvent: false });
			}
		});

		// Update document validators
		effect(() => {
			const legalFoundationId = this.legalFoundationId();
			const available = this.availableLegalFoundation();
			const docsCtrl = this.form.get('documents');

			if (!legalFoundationId) {
				this.setInitialStatementVisibility(false);
			} else {
				void this.loadValidTemplateAmount(legalFoundationId);
			}

			if (!docsCtrl) return;

			const baseValidators = [
				maxFiles(this.maxFileCount),
				maxFileSize(this.maxFileSize),
				allowedFileTypes(this.environment.fileSettings.allowedMimeTypes),
			];

			if (!legalFoundationId) {
				this.documentsRequired.set(false);
				docsCtrl.setValidators(baseValidators);
				docsCtrl.setErrors(null);
				docsCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
				return;
			}

			const selectedLegalFoundation = available.find((foundation) => foundation.id === legalFoundationId);
			const requiresDocumentsForSos = !!selectedLegalFoundation?.needsDocumentForSos;

			const shouldRequireDocuments = requiresDocumentsForSos && !this.isSubmit() && !this.isReturn();

			this.documentsRequired.set(shouldRequireDocuments);

			if (shouldRequireDocuments) {
				docsCtrl.setValidators([this.requiredFileValidator, ...baseValidators]);
			} else {
				docsCtrl.setValidators(baseValidators);
				docsCtrl.setErrors(null);
			}

			docsCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		});

		// Valid Clarification Date
		effect(() => {
			const assignmentId = this.assignmentId();
			if (!assignmentId) return;
			void this.loadValidClarificationDateRange(assignmentId);
		});
	}

	public getDisabilityKindName(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.disabilityType?.remark ?? '';
	}

	public getDisabilityKindDifferentiation(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.categoryDiagnosis?.description ?? '-';
	}

	public hasFileError(control: AbstractControl | null, ...errors: string[]) {
		if (!control) return '';

		const error = errors.find(
			(err) => control.hasError(err) && (control.dirty || !!control.value?.length || control.touched),
		);

		return error ?? '';
	}

	public readonly queryParams = {
		redirectUrl: this.router.url,
	};

	public saveDraftBeforeNavigation() {
		this.draftService.saveDraft('assignment', this.draftId(), this.form.getRawValue());
	}

	public cancel() {
		this.draftService.deleteDraft('fds', this.draftId());
		this.navigateAfterFinished(['../', 'fds'], { relativeTo: this.route });
	}

	public async save() {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const {
			documents,
			careRelatedHandicapId,
			endOfCareId,
			clarification,
			sponsorId,
			legalFoundationId,
			initialStatement,
			dateOfCommission,
		} = this.form.getRawValue();
		const documentIds = documents.map((doc) => doc.id);

		const clar = clarification ?? {
			hasBeenClarified: false,
			clarifiedAt: undefined,
			typeOfClarification: undefined,
		};

		const originalAssignment = this.assignment();
		if (!originalAssignment) return;

		const isLegacyClarification = this.isLegacyClarification(originalAssignment);
		const payload: UpdateAssignmentBody & { documentIds?: string[] } = {
			careRelatedHandicapId: careRelatedHandicapId ?? undefined,
			endOfCareId: endOfCareId ? endOfCareId : undefined,
			documentIds: Array.from(new Set(documentIds)),
			socialDataProtectionInfo: isLegacyClarification
				? originalAssignment.socialDataProtectionInfo
				: clar.hasBeenClarified,
			hasBeenClarified: clar.hasBeenClarified,
			clarifiedAt: clar.hasBeenClarified ? clar.clarifiedAt : undefined,
			typeOfClarification: clar.hasBeenClarified ? clar.typeOfClarification : undefined,

			sponsorId: sponsorId ?? undefined,
			legalFoundationId: legalFoundationId ?? undefined,
			careStartDate: dateOfCommission ?? undefined,
		};

		this.isLoading.set(true);
		try {
			await this.assignmentApi.update(this.clientId()!, this.assignmentId(), payload, initialStatement ?? undefined);
			await this.state.updateAssignments();
			this.draftService.deleteDraft('fds', this.draftId());
			await this.navigateAfterFinished(['../', 'fds'], { relativeTo: this.route });
			this.notificationService.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.fds_edit_success')),
			);
		} catch (error) {
			console.error(error);
			this.notificationService.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.fds_edit_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	}

	/**
	 * Returns 'true' once the data protection clarification value was already given.
	 */
	public readonly isClarificationFinal = computed(() => {
		const a = this.assignment();
		return !!(a?.hasBeenClarified && a?.clarifiedAt && a?.typeOfClarification != undefined);
	});

	private isLegacyClarification(assignment?: AssignmentWithRelations): boolean {
		if (!assignment) return false;

		return (
			assignment.socialDataProtectionInfo === true &&
			!assignment.hasBeenClarified &&
			!assignment.clarifiedAt &&
			assignment.typeOfClarification == null
		);
	}

	private getEffectiveClarification(assignment?: AssignmentWithRelations): ClarificationValue {
		if (!assignment) {
			return {
				hasBeenClarified: false,
				clarifiedAt: undefined,
				typeOfClarification: undefined,
			};
		}

		return {
			hasBeenClarified: assignment.hasBeenClarified || assignment.socialDataProtectionInfo || false,
			clarifiedAt: assignment.clarifiedAt ?? undefined,
			typeOfClarification: assignment.typeOfClarification ?? undefined,
		};
	}

	private async loadValidClarificationDateRange(assignmentId: number) {
		try {
			const range = await this.validationApi.getValidClarificationDateRange(assignmentId);
			this.clarificationDateRange.set(range);
		} catch (error) {
			console.error('Fehler beim Laden des gültigen Zeitraums für das Merkblatt Sozialdatenschutz', error);
			this.clarificationDateRange.set(undefined);
		}
	}

	private async loadTypeOfCares() {
		const res = await this.categoriesApi.getAllTypesOfCare(
			this.environment.pagination.offset,
			this.environment.pagination.range,
			true,
			false,
			false,
		);
		const items = res.typesOfCare ?? [];
		this.typeOfCares.set(items);
		this.typeOfCareId.set(items[0]?.id);
	}

	private async loadSponsors(typeOfCareId?: number) {
		if (!typeOfCareId) {
			this.sponsors.set([]);
			return;
		}

		const res = await this.sponsorService.getAllSponsorsForTypeOfCare(typeOfCareId);
		this.sponsors.set(res.entities ?? []);
	}

	private async getFilteredLegalFoundations(typeOfCareId?: number, sponsorId?: number) {
		const legalFoundationRange = this.environment.pagination.range;
		if (!typeOfCareId || !sponsorId) {
			this.availableLegalFoundation.set([]);
			return;
		}

		const res = await this.legalFoundationService.getAllForTypeOfCareAndSponsor(
			typeOfCareId,
			sponsorId,
			undefined,
			legalFoundationRange,
		);
		const all = (res?.entities ?? res?.entities ?? []) as LegalFoundation[];
		this.availableLegalFoundation.set(all);
	}

	private canEditControl(controlName: string): boolean {
		const meta = this.editableCtrls();

		if (meta.includes('*')) {
			return true;
		}

		const metaValueOfControl = mapEditFormFieldNamesToApiAssignmentFieldNames(controlName);

		return meta.some((field) => field.toLowerCase() === metaValueOfControl.toLowerCase());
	}

	private readonly requiredFileValidator: ValidatorFn = (control) => {
		const value = control.value;

		if (!value || !Array.isArray(value) || value.length === 0) {
			return { requiredFile: true };
		}

		return null;
	};

	private async loadValidTemplateAmount(legalFoundationId: number) {
		try {
			const amount = await this.legalFoundationService.getValidTemplatesAmount(legalFoundationId);
			this.setInitialStatementVisibility(amount > 1);
		} catch (error) {
			this.setInitialStatementVisibility(false);
			this.errorService.handleError(error, false);
		}
	}

	private setInitialStatementVisibility(visible: boolean) {
		this.showInitialStatementSelect.set(visible);
		const ctrl = this.form.get('initialStatement');
		if (!ctrl) return;
		if (visible) {
			ctrl.setValidators([Validators.required]);
		} else {
			ctrl.clearValidators();
		}
		ctrl.setValue(null);
		ctrl.updateValueAndValidity({ onlySelf: true });
	}
}
